import type { DerivedPageAssetSlot, SinglePageAssetSlot } from "~/lib/assets/slots";
import type { ImageVariant } from "~/lib/images/variants";

import { AssetPlaceholder, warnEmptyAssetSlot } from "./AssetPlaceholder";
import { usePageAsset } from "./PageAssetsProvider";
import { SlotImage, type SlotImageProps } from "./SlotImage";
import { SlotVideo } from "./SlotVideo";

/**
 * T258 — render a managed page slot that accepts EITHER an image or a video,
 * picking the element from the uploaded asset's `kind` ("si lleva imagen se ve
 * imagen, si lleva vídeo se ve vídeo"). Used by any `expects: "either"` placement
 * (today: the landing hero `hero-landing`). Delegates to the existing primitives so the image
 * (`<Image>` anti-CLS + 404 fallback) and video (Stream background + reduced-motion
 * poster) paths keep their exact behavior — image and video legitimately need
 * different CSS, hence separate class props.
 */
export type SlotMediaProps = {
  slot: SinglePageAssetSlot | DerivedPageAssetSlot;
  /** Image variant for the image branch (drives anti-CLS sizing). */
  variant: ImageVariant;
  /** Decorative full-bleed background mode for the video branch (muted/autoplay/loop). */
  background?: boolean;
  /** Class for the video / poster element. */
  videoClassName?: string;
  /** Class for the image element (and the empty-state placeholder). */
  imageClassName?: string;
  loading?: SlotImageProps["loading"];
  fetchPriority?: SlotImageProps["fetchPriority"];
  responsive?: SlotImageProps["responsive"];
  sizes?: SlotImageProps["sizes"];
  /**
   * T620 — a second, video-only placement holding the NATIVE portrait master of the
   * same scene. Forwarded to `SlotVideo` for the video branch only: an image in `slot`
   * has no portrait twin (the design's two masters are both video).
   */
  portraitSlot?: SinglePageAssetSlot;
};

export function SlotMedia({
  slot,
  variant,
  background,
  videoClassName,
  imageClassName,
  portraitSlot,
  loading,
  fetchPriority,
  responsive,
  sizes,
}: SlotMediaProps) {
  const asset = usePageAsset(slot);

  if (asset?.kind === "video") {
    return (
      <SlotVideo slot={slot} background={background} className={videoClassName} portraitSlot={portraitSlot} />
    );
  }
  if (asset?.kind === "image") {
    return <SlotImage slot={slot} variant={variant} className={imageClassName}
      loading={loading} fetchPriority={fetchPriority} responsive={responsive} sizes={sizes} />;
  }

  warnEmptyAssetSlot(slot, asset ? "unsupported asset kind for this slot" : "no published asset");
  // imageClassName is the placeholder anchor (videoClassName carries no fallback
  // image box); fall back to videoClassName so a video-targeted `either` slot still
  // reserves the right space if AssetKind ever grows beyond image|video.
  return <AssetPlaceholder variant={variant} className={imageClassName ?? videoClassName} />;
}
